import React from "react";
import PropTypes from "prop-types";
import * as COMMON from "../components/Common";
import * as CONFIG from "./configState";
import {
    addressNotSetWarning,
    createConditionFunction,
    getMaxPostHeight,
    getPos,
    insulatedSpanWarning,
    interpolate,
    postFootingWarning,
    postHeightWarning,
    postNotchedWarning,
    singleSpanWarning,
} from "./warningHelper";
import { SHOW_DESIGNER_MESSAGE, validateFullJob } from "./jobState_Validation";
import { toggleDesigner } from "./appState";
import { updateAreaSpecificationOnly } from "./jobState";

// Callback function
function allAreaSpecifications(job, callback) {
    for (let index in job.AreaSpecifications) {
        let areaIndex = Number(index);
        const this_area = job.AreaSpecifications[Number(areaIndex)];

        // Call the callback function and pass the necessary values
        callback(this_area, areaIndex);
    }
}

function insulatedMultispanWarning({ job, engValid, this_area, areaIndex }) {
    let is_flat = false;
    if (this_area.SHAPE.DesignType === CONFIG.DESIGN_TYPES.FLAT) is_flat = true;

    if (
        is_flat &&
        this_area?.ROOF_SHEETING?.RoofSheetData?.["InsulatedOverspan"] &&
        this_area?.ROOF_SHEETING?.RoofSheetData?.PressureTableMaxSpanSingle &&
        !engValid
    ) {
        let sheetName, length, maxSpan, roofsheet;
        maxSpan = this_area?.ROOF_SHEETING?.RoofSheetData?.PressureTableMaxSpanDouble;
        sheetName = this_area?.ROOF_SHEETING?.RoofSubType;
        length = this_area.SHAPE.Depth;
        return insulatedSpanWarning({ job, sheetName, length, areaIndex, this_area, roofsheet, maxSpan });
    }
}

function jobAddressNotSet({ job, dispatch }) {
    if (
        job &&
        job?.Customer?.Address?.FormattedAddress === "" &&
        job?.SiteParameters?.Address?.FormattedAddress === "" &&
        job?.SiteParameters?.WindData?.WindZoneAS1170?.Latlng_Short !== ""
    )
        return addressNotSetWarning({ dispatch });
}

function insulatedSinglespanWarning({ job, engValid, this_area, areaIndex }) {
    const roofsheet_data = this_area?.ROOF_SHEETING;
    if (roofsheet_data?.RoofSheetData?.single_span_only && window.brand === "Lysaght") {
        //secondary check for potential roof sheet overspan on single span sheets
        let width = this_area?.META?.RoofSheetSpan;

        if (this_area.SHAPE.SheetDirection === "Left" || this_area.SHAPE.SheetDirection === "Right")
            width = width - this_area.SHAPE.OH_Left - this_area.SHAPE.OH_Right;
        else width = width - this_area.SHAPE.OH_Front - this_area.SHAPE.OH_Back;
        width = width / Math.cos(COMMON.DegreesToRadians(this_area.SHAPE.RoofPitch));
        if (width && width > 1000) {
            let max_span = roofsheet_data?.RoofSheetData?.max_internal_span;

            if (max_span && width - 65 > max_span) {
                let sheetName, length, maxSpan, roofsheet;
                maxSpan = max_span;
                sheetName = this_area?.ROOF_SHEETING?.RoofSubType;
                length = this_area.SHAPE.Depth;
                return singleSpanWarning({ job, sheetName, length, areaIndex, this_area, roofsheet, maxSpan });
            }
        }
    }
}

function insulatedDutchHipWarning(job, areaIndex, config, dispatch) {
    const areaShape = job?.AreaSpecifications?.[areaIndex]?.SHAPE;
    const hasInsulatedPanel = job?.AreaSpecifications?.[areaIndex]?.META?.HasInsulatedPanel || false;
    const warning = {
        title: "Insulated panel on hip or dutch",
    };

    const ignoreWarning = checkIgnoreWarning({ job, areaIndex, warning });

    if (areaShape?.DesignType !== "Gable Hip Roof" || !hasInsulatedPanel) {
        if (ignoreWarning) {
            removeIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app: null, validate: false });
        }

        return;
    }

    const roofType1 = areaShape?.RoofType1;
    const roofType2 = areaShape?.RoofType2;

    const isDutchOrHip =
        roofType1 === "Dutch Gable" || roofType1 === "Hip" || roofType2 === "Dutch Gable" || roofType2 === "Hip";

    if (!isDutchOrHip) {
        if (ignoreWarning) {
            removeIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app: null, validate: false });
        }

        return;
    }

    if (ignoreWarning) {
        return;
    }

    return {
        error: true,
        title: "Insulated panels with dutch or hip ends are for experienced builders only",
        message: (
            <>
                Careful cutting and fixing of panels is required at hips. The Builder will need to adjust on site as hip
                sheets are not supplied cut to angles.
            </>
        ),
        button1Id: "Continue-Acknowledge",
        primaryActionText: "Acknowledge",
        primaryAction: () => setIgnoreAreaWarning({ areaIndex, dispatch, warning, job, app: null, validate: false }),
    };
}

function maxPostHeightWarning({
    job,
    engValid,
    this_area,
    areaIndex,
    config,
    app,
    user,
    confirmEngineeringAction,
    overrideEngineeringAction,
}) {
    // Check max height for posts
    const isFlanged =
        job?.AreaSpecifications[areaIndex].BEAMS_AND_POSTS.CornerPostsFullHeight === 2 ||
        job?.AreaSpecifications[areaIndex].BEAMS_AND_POSTS.IntermediatePostsFullHeight === 2;

    let product = config.post_types[job?.AreaSpecifications[areaIndex]?.BEAMS_AND_POSTS?.PostType]?.["Product Code"];

    if (!product) return;

    // Check if the post if flanged.
    if (isFlanged) {
        product += "F";
    }

    // Initialize findPostProduct as an empty array
    let findPostProduct = [];

    if (app && app.pricefile && Array.isArray(app.pricefile.data)) {
        findPostProduct = app.pricefile.data.filter((e) => product === e.ProductIndex);
    }

    // get POA
    let currentPOA = "SL_" + job?.POA;

    let postChanges = job?.AreaSpecifications[areaIndex]?.POSTMODIFICATIONS;
    let footingChanges = job?.AreaSpecifications[areaIndex]?.Footings;
    let currentPosts = {};

    // extra cut as 500 for post all in concrete no post changes.
    let extraCut = 0;

    if (
        (footingChanges?.length === 0 || footingChanges === undefined) &&
        (postChanges?.length === 0 || postChanges === undefined)
    ) {
        extraCut = 0;
    } else {
        // Loop through footings and posts

        if (footingChanges)
            for (var foot of footingChanges) {
                const ref = foot?.Ref2 ? foot.Ref2 : foot.Ref;
                currentPosts[ref] = { Ref: ref, footing: foot.Kind };
            }

        if (postChanges)
            for (var post of postChanges) {
                const ref = post?.Ref2 ? post?.Ref2 : post?.Ref;
                if (currentPosts[ref]) {
                    currentPosts[ref]["post"] =
                        post.Section === ""
                            ? job?.AreaSpecifications[areaIndex].BEAMS_AND_POSTS.PostType
                            : post.Section;
                    currentPosts[ref]["Length"] = post?.Length;
                } else {
                    currentPosts[ref] = {
                        Ref: ref,
                        post:
                            post.Section === ""
                                ? job?.AreaSpecifications[areaIndex].BEAMS_AND_POSTS.PostType
                                : post.Section,
                        Length: post.Length,
                    };
                }
            }
    }

    // Check default posts
    if (findPostProduct[0]?.PromptTextCodes) {
        const pos = getPos({ currentPOA, findPostProduct });

        if (pos > -1) {
            let productName;
            for (var post_type of Object.values(config.post_types)) {
                if (post_type["Product Code"] === findPostProduct[0]?.ProductIndex) {
                    productName = job?.AreaSpecifications[areaIndex].BEAMS_AND_POSTS.PostType;
                    break;
                }
            }

            const maxPostHeight = getMaxPostHeight({ findPostProduct, pos, currentPOA, extraCut });

            let warning = postHeightWarning({
                job,
                pos,
                maxPostHeight,
                confirmEngineeringAction,
                user,
                overrideEngineeringAction,
                postName: productName,
                engValid,
            });

            if (warning) {
                return warning;
            }
        }
    }

    // Check Changed Posts
    if (window.LYSAGHT)
        for (var _post of Object.keys(currentPosts)) {
            if (currentPosts[_post]?.post) {
                // Find changed post
                let productCode = config?.post_types?.[currentPosts[_post]?.post]?.["Product Code"];
                if (!productCode) {
                    continue;
                }

                // Check if the post if flanged.
                if (isFlanged) {
                    productCode += "F";
                }

                let findChangedPostProduct = app.pricefile.data.filter((e) => productCode === e.ProductIndex);

                let productName = currentPosts[_post]?.post;

                // Check if allowed notched and notched selected
                if (!config.post_types[currentPosts[_post]?.post].allow_notched && isFlanged) {
                    let warning = postNotchedWarning({
                        postName: productName,
                        areaIndex,
                        this_area,
                    });

                    if (warning) {
                        return warning;
                    }
                }

                // Check if footing change allowed to be flanged ("Bolt to slab") with current post
                if (
                    currentPosts[_post]?.footing &&
                    currentPosts[_post]?.footing !== "Concrete In" &&
                    !config.post_types[currentPosts[_post]?.post].allow_notched &&
                    !config.post_types[currentPosts[_post]?.post]?.Base_Plate_Code_Flat_Internal
                ) {
                    let warning = postFootingWarning({
                        areaIndex,
                        this_area,
                        footingType: currentPosts[_post]?.footing,
                        postName: productName,
                        post: _post,
                    });

                    if (warning) {
                        return warning;
                    }
                }

                // Only check if there are promptcodes
                if (findChangedPostProduct[0]?.PromptTextCodes) {
                    const pos = getPos({ currentPOA, findPostProduct: findChangedPostProduct });

                    if (pos > -1) {
                        if (currentPosts[_post]?.footing) {
                            if (currentPosts[_post].footing !== "Concrete In") {
                                extraCut = 0;
                            } else extraCut = 500; // Change to default footing
                        }

                        const maxPostHeight = getMaxPostHeight({
                            findPostProduct: findChangedPostProduct,
                            pos,
                            currentPOA,
                            extraCut,
                        });

                        let warning = postHeightWarning({
                            job,
                            pos,
                            maxPostHeight,
                            confirmEngineeringAction,
                            user,
                            overrideEngineeringAction,
                            postName: productName,
                            height: currentPosts[_post].Length,
                            engValid,
                        });

                        if (warning) {
                            return warning;
                        }
                    }
                }
            } else {
                // Only a footing change.
                // Check if footing change allowed to be flanged ("Bolt to slab") with default post
                if (
                    currentPosts[_post]?.footing &&
                    currentPosts[_post]?.footing !== "Concrete In" &&
                    !config.post_types[currentPosts[_post]?.post]?.allow_notched &&
                    !config.post_types[job?.AreaSpecifications[areaIndex]?.BEAMS_AND_POSTS?.PostType]
                        ?.Base_Plate_Code_Flat_Internal
                ) {
                    let warning = postFootingWarning({
                        areaIndex,
                        this_area,
                        footingType: currentPosts[_post]?.footing,
                        postName: job?.AreaSpecifications[areaIndex]?.BEAMS_AND_POSTS?.PostType,
                        post: _post,
                    });

                    if (warning) {
                        return warning;
                    }
                }
            }
        }
}

// When set, the software will ignore these warnings in the future for that AreaIndex.
function setIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app, validate = true }) {
    let new_job = COMMON.deepCopy(job);

    if (!new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS) {
        new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS = [];
    }

    // Find the index of the warning with the same title
    const warningIndex = new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS.findIndex(
        (ignoredWarning) => ignoredWarning.title === warning.title
    );

    if (warningIndex !== -1) {
        // Replace the existing warning with the new one
        new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS[warningIndex] = warning;
    } else {
        // Add the new warning if it doesn't already exist
        new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS.push(warning);
    }

    if (validate) {
        // Forced compliance/mass settings, updates
        new_job = validateFullJob(config, new_job, -1, app);

        dispatch({
            type: "NEW_JOB",
            parameter: new_job,
        });
    } else {
        dispatch(
            updateAreaSpecificationOnly(
                areaIndex,
                "IGNORED_WARNINGS",
                null,
                new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS
            )
        );
    }
}

// Remove ignored warning from AreaSpecifications[areaIndex].IGNORED_WARNINGS
function removeIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app, validate = true }) {
    let new_job = COMMON.deepCopy(job);

    if (new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS) {
        new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS = new_job.AreaSpecifications[
            areaIndex
        ].IGNORED_WARNINGS.filter((ignoredWarning) => ignoredWarning.title !== warning.title);

        if (validate) {
            // Forced compliance/mass settings, updates
            new_job = validateFullJob(config, new_job, -1, app);

            dispatch({
                type: "NEW_JOB",
                parameter: new_job,
            });
        } else {
            dispatch(
                updateAreaSpecificationOnly(
                    areaIndex,
                    "IGNORED_WARNINGS",
                    null,
                    new_job.AreaSpecifications[areaIndex].IGNORED_WARNINGS
                )
            );
        }
    }
}

function checkIgnoreWarning({ job, areaIndex, warning }) {
    const ignoredWarnings = job?.AreaSpecifications[areaIndex].IGNORED_WARNINGS;

    if (ignoredWarnings) {
        const foundWarning = ignoredWarnings.find((ignoredWarning) => ignoredWarning.title === warning.title);

        if (foundWarning) {
            return { ...foundWarning };
        }
    }

    return null;
}

function sidesBlockedToAttached({ job, areaIndex, config, app, dispatch }) {
    const blockingCount = job?.SiteParameters?.WindData?.Blocking || 0;

    const attachmentCount = job.AreaSpecifications.reduce((count, area, index) => {
        const attachments = job?.AreaSpecifications?.[areaIndex]?.ATTACHMENTS || {};
        return (
            count +
            (attachments.AttachmentLeft || 0) +
            (attachments.AttachmentRight || 0) +
            (attachments.AttachmentBottom || 0) +
            (attachments.AttachmentTop || 0)
        );
    }, 0);

    const blockingVsAttachment = `${blockingCount},${attachmentCount}`;

    const warning = {
        title: "Blocked to Attached Warning",
        detail: blockingVsAttachment,
    };

    const ignoreWarning = checkIgnoreWarning({ job, areaIndex, warning });

    if (ignoreWarning && ignoreWarning?.detail === blockingVsAttachment) {
        return;
    }

    if (blockingCount < attachmentCount) {
        return {
            error: true,
            title: "Blocked to Attached Warning",
            message: (
                <>
                    The number of sides blocked ({blockingCount}) is less than the number of sides attached (
                    {attachmentCount}). This may affect the structural integrity of your design.
                    <br />
                    Please adjust your design's blocking, or choose to acknowledge and ignore this warning.
                </>
            ),
            button1Id: "Continue-Ignore",
            primaryActionText: "Edit Blocking",
            primaryAction: () => {
                dispatch(toggleDesigner("showCustomerJobPanel"));
            },
            secondaryActionText: "Ignore warning",
            secondaryAction: () =>
                setIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app, validate: false }),
        };
    }

    // Current blocking is valid. Remove existing warning
    if (ignoreWarning) {
        removeIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app, validate: false });
    }
}

function insetDefaultWarning({ job, config, this_area, areaIndex, app, dispatch }) {
    if (
        window.FIELDERS &&
        job?.AreaSpecifications[areaIndex]?.BEAMS_AND_POSTS?.HasRidgeBeam === 0 &&
        job?.AreaSpecifications[areaIndex]?.META?.HasInsulatedPanel
    ) {
        let new_job = COMMON.deepCopy(job);
        new_job.AreaSpecifications[areaIndex].BEAMS_AND_POSTS.HasRidgeBeam = 1;

        dispatch({
            type: SHOW_DESIGNER_MESSAGE,
            message: "Purlins Inset was selected because roofsheets with insulated panels require inset purlins.",
        });

        //forced compliance/ mass settings, updates
        new_job = validateFullJob(config, new_job, -1, app);
        dispatch({
            type: "NEW_JOB",
            parameter: new_job,
        });
    }
}

function gableFlyoverWarning({ job, config, this_area, areaIndex, app, dispatch }) {
    if (
        window.LYSAGHT &&
        job?.AreaSpecifications[areaIndex]?.SHAPE?.DesignType.toLowerCase().includes("gable") &&
        job?.AreaSpecifications[areaIndex]?.ATTACHMENTS?.AttachmentModifications.some((mod) =>
            mod["AttachmentType"].toLowerCase().includes("flyover")
        )
    ) {
        let new_job = COMMON.deepCopy(job); // Ensure the job is deeply copied

        // Use map to create a new array of updated AttachmentModifications
        new_job.AreaSpecifications[areaIndex].ATTACHMENTS.AttachmentModifications = new_job.AreaSpecifications[
            areaIndex
        ].ATTACHMENTS.AttachmentModifications.map((mod) => {
            if (mod["AttachmentType"].toLowerCase().includes("flyover")) {
                return { ...mod, AttachmentType: "Rafter Brackets supporting Beam" };
            }
            return mod; // Return the original if no modification is needed
        });

        // Dispatch the warning message
        dispatch({
            type: SHOW_DESIGNER_MESSAGE,
            message: "Flyover is not permitted on gables, attachment/s have been set to Rafter Brackets.",
        });

        // Re-validate the job and dispatch updated job
        new_job = validateFullJob(config, new_job, -1, app);
        dispatch({
            type: "NEW_JOB",
            parameter: new_job,
        });
    }
}

function overhangPVCdownpipe({ job, areaIndex, config, app, dispatch }) {
    const shape = job?.AreaSpecifications[areaIndex]?.SHAPE;

    const standardOverhangs = {
        OH_Back: 0,
        OH_Back_Sheet: 0,
        OH_Front: 0,
        OH_Front_Sheet: 0,
        OH_Left: 0,
        OH_Left_Sheet: 0,
        OH_Right: 0,
        OH_Right_Sheet: 0,
    };

    let overhangCount = 0;
    for (const key in standardOverhangs) {
        if (shape && shape.hasOwnProperty(key)) {
            overhangCount += shape[key];
        }
    }

    const recommended_downpipe = window.LYSAGHT ? "Lysaght 90mm Round PVC Downpipe" : "90mm Round PVC Downpipe";
    const warning = {
        title: "Roofsheets with Overhang should have PVC downpipes",
        recommendedValue: recommended_downpipe,
    };

    let ignoreWarning = checkIgnoreWarning({ job, areaIndex, warning });

    // Overhangs were changed and not PVC selected
    if (
        overhangCount !== 0 &&
        !job?.AreaSpecifications[areaIndex]?.GUTTERS_AND_DOWNPIPES?.DownpipeType.includes("PVC") &&
        app?.user?.id &&
        !app?.user?.id.includes("diego.portela@bluescopesteel.com")
    ) {
        // Check if warning is already ignored
        if (ignoreWarning) return;
        return {
            error: true,
            title: "Roofsheets with Overhang should have PVC downpipes",
            message: [<>'{recommended_downpipe}' is recommended with sheet overhang.</>],
            primaryActionText: `Set to '${recommended_downpipe}'`,
            primaryAction: () => {
                let new_job = COMMON.deepCopy(job);
                new_job.AreaSpecifications[areaIndex].GUTTERS_AND_DOWNPIPES.DownpipeType = recommended_downpipe;

                //forced compliance/ mass settings, updates
                new_job = validateFullJob(config, new_job, -1, app);
                dispatch({
                    type: "NEW_JOB",
                    parameter: new_job,
                });
            },
            secondaryActionText: "Add Ignore",
            secondaryAction: setIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app }),
        };
    }

    // Current selection is valid. Remove existing warning
    if (ignoreWarning) {
        removeIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app });
    }
}

function apolloFireWarning({ job, areaIndex, config, app, dispatch }) {
    const warning = {
        title: "Non-compliance with Bushfire Attack Level (BAL) requirements",
    };

    const roofSheetType = job?.AreaSpecifications?.[areaIndex]?.ROOF_SHEETING?.RoofType;
    const isApollo = typeof roofSheetType === "string" && roofSheetType.toLowerCase().includes("apollo");
    const ignoreWarning = checkIgnoreWarning({ job, areaIndex, warning });

    if (!isApollo && ignoreWarning) {
        removeIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app, validate: false });
        return;
    }

    if (!isApollo || ignoreWarning) return;

    const message =
        "Apollo Insulated panel products are not compliant with Bushfire Attack Level (BAL) requirements as outlined under AS3959-2018. " +
        "Please confirm with the Local Council/Shire that the selected location is not subject to these requirements.";

    return {
        error: true,
        title: warning.title,
        message: [<>{message}</>],
        primaryActionText: "Acknowledge",
        primaryAction: setIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app, validate: false }),
    };
}

function parallelDesignWarning({ job, areaIndex, config, app, dispatch }) {
    const areaCount = job?.AreaSpecifications?.length || 0;
    const roofSheetDirection = job?.AreaSpecifications?.[areaIndex]?.SHAPE?.SheetDirection;
    const isParallel = roofSheetDirection === "Left" || roofSheetDirection === "Right";
    const shouldWarn = isParallel && areaCount === 1 && areaIndex === 0;

    const warning = {
        title: "Parallel roof sheet direction is not recommended",
    };

    const ignoreWarning = checkIgnoreWarning({ job, areaIndex, warning });

    if (!shouldWarn && ignoreWarning) {
        removeIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app, validate: false });
        return;
    }

    if (!shouldWarn || ignoreWarning) return;

    const message =
        "This roof sheet configuration is not recommended for single-area designs. " +
        "A parallel roof sheet direction is best suited for multi-area designs to ensure optimal water drainage.";

    return {
        error: true,
        title: warning.title,
        message: [<>{message}</>],
        primaryActionText: "Acknowledge",
        primaryAction: setIgnoreAreaWarning({ areaIndex, dispatch, warning, job, config, app, validate: false }),
    };
}

// These are warnings that are checked at the end of the system.
// Can be bypassed with engineering override, Can continue.
export function jobWarningPopups(props) {
    let warnings;
    // allAreaSpecifications(props.job, (this_area, areaIndex) => {
    //     // Add warnings here...
    // });

    return warnings; // Return the error, note this will work top down of all warnings
}

// These are Errors that are checked at the end of the system.
// Can be bypassed with engineering override, but default is to block from continuing.
export function jobReadyStateErrorPopups(props) {
    let errors;

    let template_primaryAction = ({ primaryActionString, props, this_area, areaIndex }) => {
        let new_job = COMMON.deepCopy(props.job);

        const primaryConditions = createConditionFunction(primaryActionString);
        primaryConditions({ ...props, this_area, areaIndex, new_job }); // Updates new_job

        //forced compliance/ mass settings, updates
        new_job = validateFullJob(props.config, new_job, -1, props.app);
        props.dispatch({
            type: "NEW_JOB",
            parameter: new_job,
        });
    };
    let error_template = {
        error: true,
        title: "",
        message: [<></>],
        primaryActionText: "Set to",
        secondaryActionText: "Cancel",
        tertiaryActionText: props.user.Role >= 91 ? "Authorise Override?" : "",
        tertiaryAction: props.user.Role >= 91 ? props.overrideEngineeringAction : {},
        primaryAction: null,
    };

    allAreaSpecifications(props.job, (this_area, areaIndex) => {
        // Add Errors here...
        errors = maxPostHeightWarning({ ...props, this_area, areaIndex }) || errors;
        errors = insulatedMultispanWarning({ ...props, this_area, areaIndex }) || errors;
        errors = jobAddressNotSet({ ...props, this_area, areaIndex }) || errors;
        errors = insulatedSinglespanWarning({ ...props, this_area, areaIndex }) || errors;

        const allWarnings = props?.config?.all_warnings;
        if (allWarnings?.jobStateError) {
            const warning = allWarnings?.jobStateError;

            for (const type in warning) {
                if (warning.hasOwnProperty(type) && type !== "info") {
                    const conditionStrings = warning[type]?.conditions;
                    const condition = createConditionFunction(conditionStrings);
                    const message = warning[type]?.message;
                    const title = warning[type]?.title;
                    const primaryActionString = warning[type]?.primaryAction;
                    const primaryActionText = warning[type]?.primaryText;

                    if (condition({ ...props, this_area, areaIndex })) {
                        error_template["title"] = title;
                        error_template["message"] = (
                            <>{interpolate(message, { props: { ...props, this_area, areaIndex } })}</>
                        );
                        error_template["primaryActionText"] = interpolate(primaryActionText, {
                            props: { ...props, this_area, areaIndex },
                        });
                        error_template["primaryAction"] = () =>
                            template_primaryAction({
                                props,
                                primaryActionString,
                                this_area,
                                areaIndex,
                            });
                        errors = error_template;
                    }
                }
            }
        }
    });

    return errors; // Return the error, note this will work top down of all Errors
}

// These are Errors that are checked with any change in the system.
// Can not be bypassed with engineering override.
export function jobStateErrorPopups(props) {
    let errors;

    if (props?.app?.user?.id.includes("diego.portela")) return errors;

    allAreaSpecifications(props.job, (this_area, areaIndex) => {
        if (this_area.SHAPE.DesignType === CONFIG.DESIGN_TYPES.LOUVRE) {
        } else {
            // Add Errors here...
            insetDefaultWarning({ ...props, this_area, areaIndex });
            gableFlyoverWarning({ ...props, this_area, areaIndex });
            errors = overhangPVCdownpipe({ ...props, this_area, areaIndex }) || errors;
            errors = sidesBlockedToAttached({ ...props, this_area, areaIndex }) || errors;
            errors = insulatedDutchHipWarning(props.job, areaIndex, props.config, props.dispatch) || errors;
            errors = apolloFireWarning({ ...props, this_area, areaIndex }) || errors;
            errors = parallelDesignWarning({ ...props, this_area, areaIndex }) || errors;
        }
    });

    return errors; // Return the error, note this will work top down of all Errors
}

/// Message POPUPS
// These are checked before job validation
export function jobStateMessagePopups(props) {
    allAreaSpecifications(props.job, (this_area, areaIndex) => {
        // Add Warnings here...
        const allWarnings = props?.config?.all_warnings;
        if (allWarnings?.jobStateMessagePopups) {
            const warning = allWarnings?.jobStateMessagePopups;

            for (const type in warning) {
                if (warning.hasOwnProperty(type) && type !== "info") {
                    const conditionStrings = warning[type]?.conditions;
                    const condition = createConditionFunction(conditionStrings);
                    const message = warning[type]?.message;

                    if (condition({ ...props, this_area, areaIndex })) {
                        props.dispatch({
                            type: SHOW_DESIGNER_MESSAGE,
                            message: message,
                        });
                    }
                }
            }
        }
    });
}

jobWarningPopups.propTypes = {
    job: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    app: PropTypes.object.isRequired,
};

jobStateMessagePopups.propTypes = {
    job: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    dispatch: PropTypes.object.isRequired,
    section: PropTypes.string.isRequired,
    value: PropTypes.object.isRequired,
};

jobReadyStateErrorPopups.propTypes = {
    job: PropTypes.object.isRequired,
    engValid: PropTypes.bool.isRequired,
    dispatch: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    app: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    confirmEngineeringAction: PropTypes.func.isRequired,
    overrideEngineeringAction: PropTypes.func.isRequired,
};

jobStateErrorPopups.propTypes = {
    job: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    app: PropTypes.object.isRequired,
    dispatch: PropTypes.object.isRequired,
};
